import React, { useState, useEffect } from "react";
import { useAuth } from "../../context/AuthContext";
import client from "../../lib/api";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { Switch } from "../../components/ui/switch";
import { ShieldCheck, Mail, Smartphone, Bell, User, Sparkles, KeyRound, Loader2, Check, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { DigestCard } from "../../components/DigestCard";
import { tr } from "../../i18n/LangContext";

function Row({ icon: Icon, title, desc, children }) {
  return (
    <div className="flex items-center justify-between gap-4 border-b border-white/[0.06] py-4 last:border-0">
      <div className="flex items-start gap-3">
        <div className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-white/5"><Icon className="h-4 w-4 text-brand-green" /></div>
        <div><p className="text-sm font-medium text-white">{title}</p><p className="text-xs text-muted-foreground">{desc}</p></div>
      </div>
      {children}
    </div>
  );
}

export default function SettingsView() {
  const { user } = useAuth();
  const [twoFa, setTwoFa] = useState(true);
  const [usage, setUsage] = useState(null);
  const [keyInput, setKeyInput] = useState("");
  const [savingKey, setSavingKey] = useState(false);

  const loadUsage = async () => {
    try { const res = await client.get("/ai/usage"); setUsage(res.data); } catch (e) { console.error("AI usage load failed", e); }
  };
  useEffect(() => { loadUsage(); }, []);

  const saveKey = async () => {
    if (!keyInput.trim()) { toast.error(tr("Paste your Claude API key")); return; }
    setSavingKey(true);
    try {
      const res = await client.post("/ai/key", { ai_key: keyInput.trim() });
      setUsage(res.data); setKeyInput("");
      toast.success(tr("Claude key saved"), { description: "AI now runs on your own key." });
    } catch (e) { toast.error(tr("Couldn't save key")); }
    finally { setSavingKey(false); }
  };

  const removeKey = async () => {
    try { const res = await client.delete("/ai/key"); setUsage(res.data); toast.success(tr("Key removed")); }
    catch (e) { toast.error(tr("Couldn't remove key")); }
  };

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      {/* AI Key */}
      <div className="glass rounded-2xl p-6">
        <div className="mb-1 flex items-center gap-2">
          <Sparkles className="h-5 w-5 text-brand-red" />
          <h3 className="font-display text-base font-semibold text-white">{tr("AI Key (Claude)")}</h3>
          {usage && (usage.has_own_key
            ? <span className="rounded-full border border-brand-green/30 bg-brand-green/10 px-2 py-0.5 text-[11px] text-brand-green">{tr("Your key active")}</span>
            : <span className="rounded-full border border-white/15 bg-white/5 px-2 py-0.5 text-[11px] text-muted-foreground">{tr("Free credit:")} ${usage.remaining?.toFixed(2)} {tr("left")}</span>)}
        </div>
        <p className="mb-4 text-xs text-muted-foreground">
          Every account gets <span className="text-white">$3 of free AI</span>. After that, add your own Claude (Anthropic) API key to keep running AI Deal Analysis & Airbnb estimates — billed to your own account.
        </p>
        {usage?.has_own_key ? (
          <div className="flex items-center justify-between rounded-xl border border-brand-green/20 bg-brand-green/5 p-3">
            <div className="flex items-center gap-2 text-sm text-white"><KeyRound className="h-4 w-4 text-brand-green" /> {tr("Claude key connected (sk-ant-••••)")}</div>
            <Button size="sm" variant="outline" onClick={removeKey} className="border-white/12 bg-white/[0.03] text-white hover:bg-white/[0.07]"><Trash2 className="h-4 w-4" /> {tr("Remove")}</Button>
          </div>
        ) : (
          <>
            <div className="flex flex-col gap-2 sm:flex-row">
              <Input value={keyInput} onChange={(e) => setKeyInput(e.target.value)} type="password" placeholder={tr("sk-ant-…")}
                className="bg-input/50 border-white/10 focus-visible:ring-brand-red" />
              <Button onClick={saveKey} disabled={savingKey} className="bg-brand-red hover:bg-brand-redDark text-white">
                {savingKey ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Check className="h-4 w-4" /> {tr("Save key")}</>}
              </Button>
            </div>
            <ol className="mt-3 space-y-1 text-[11px] text-muted-foreground">
              <li>{tr("1. Go to console.anthropic.com → API Keys · 2. Create & copy a key (sk-ant-…) · 3. Paste & save")}</li>
            </ol>
          </>
        )}
      </div>

      <div className="glass rounded-2xl p-6">
        <h3 className="mb-1 font-display text-base font-semibold text-white">{tr("Profile")}</h3>
        <p className="mb-5 text-xs text-muted-foreground">{tr("Manage your account details.")}</p>
        <div className="flex items-center gap-4">
          <img src={user?.avatar} alt="" className="h-16 w-16 rounded-2xl object-cover ring-1 ring-white/10" />
          <div className="grid flex-1 gap-3 sm:grid-cols-2">
            <div><label className="text-xs text-muted-foreground">{tr("Full name")}</label>
              <Input defaultValue={user?.name} className="mt-1 bg-input/50 border-white/10 focus-visible:ring-brand-red" /></div>
            <div><label className="text-xs text-muted-foreground">{tr("Email")}</label>
              <Input defaultValue={user?.email} className="mt-1 bg-input/50 border-white/10 focus-visible:ring-brand-red" /></div>
          </div>
        </div>
        <div className="mt-5 flex justify-end">
          <Button onClick={() => toast.success(tr("Profile saved"))} className="bg-brand-red hover:bg-brand-redDark text-white">{tr("Save changes")}</Button>
        </div>
      </div>

      <div className="glass rounded-2xl p-6">
        <h3 className="mb-1 font-display text-base font-semibold text-white">{tr("Security")}</h3>
        <p className="mb-3 text-xs text-muted-foreground">{tr("Two-factor authentication & sign-in.")}</p>
        <Row icon={ShieldCheck} title={tr("Two-factor authentication")} desc={tr("Require an emailed code at sign-in")}>
          <Switch checked={twoFa} onCheckedChange={(v) => { setTwoFa(v); toast(v ? "2FA enabled" : "2FA disabled"); }}
            className="data-[state=checked]:bg-brand-green" />
        </Row>
        <Row icon={Mail} title={tr("Recovery email")} desc={tr("Backup for account recovery")}>
          <span className="text-xs text-muted-foreground">{user?.email}</span>
        </Row>
        <Row icon={Smartphone} title={tr("Trusted devices")} desc={tr("2 devices remembered for 1 year")}>
          <Button variant="outline" size="sm" className="border-white/12 bg-white/[0.03] text-white hover:bg-white/[0.07]">{tr("Manage")}</Button>
        </Row>
      </div>

      <div className="glass rounded-2xl p-6">
        <h3 className="mb-1 font-display text-base font-semibold text-white">{tr("Notifications")}</h3>
        <p className="mb-3 text-xs text-muted-foreground">{tr("Choose what reaches your inbox.")}</p>
        <Row icon={Bell} title={tr("AI radar alerts")} desc={tr("New off-market leads & score changes")}>
          <Switch defaultChecked className="data-[state=checked]:bg-brand-green" />
        </Row>
        <Row icon={User} title={tr("Agent replies")} desc={tr("When an agent responds to you")}>
          <Switch defaultChecked className="data-[state=checked]:bg-brand-green" />
        </Row>
      </div>

      <DigestCard />
    </div>
  );
}
